import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string, number } from "yup";
import {
  addNewTaxType,
  updateTaxTypeRow,
  getAllTaxTypes,
} from "src/app-redux/configuration/actions/configurationActions";
import { loader, selectAllTaxTypeRowsList } from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddTaxType } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { Prompt } from "react-router-dom";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
  left0: { paddingLeft: "0px!important" },
  right0: { paddingRight: "0px!important" },
  left20: { paddingLeft: "20px!important" },
  normal: { paddingLeft: "8px!important", paddingRight: "8px!important" },
}));

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const loading = useAppSelector(loader);
  const allTaxTypeRows = useAppSelector(selectAllTaxTypeRowsList);

  const validationSchema = object({
    taxType: string().required("This field is required").max(50, "Maximum 50 digits are allowed."),
    taxRate: number()
      .required("This field is required")
      .min(1, "Minimum 1% is allowed.")
      .max(100, "Maximum 100% is allowed."),
  });

  const formik = useFormik<IAddTaxType>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      taxType: "",
      taxRate: 0,
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateTaxTypeRow(values));
      else res = await dispatch(addNewTaxType(values));
      if (res?.payload) {
        dispatch(getAllTaxTypes(0));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
      }
    },
  });

  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("taxType", params.taxType);
    formik.setFieldValue("taxRate", params.taxRate);
    formik.setFieldValue("isActive", params.isActive);
  };

  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  useEffect(() => {
    dispatch(getAllTaxTypes(0));
  }, []);

  useEffect(() => {
    if (allTaxTypeRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allTaxTypeRows]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Tax Type"
                        name="taxType"
                        placeholder="Add Tax Type"
                        disabled={false}
                        value={formik.values.taxType}
                        onChange={handleChange}
                        error={formik.touched.taxType && Boolean(formik.errors.taxType)}
                        helperText={formik.touched.taxType && formik.errors.taxType}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        label="Tax Rate (%)"
                        name="taxRate"
                        placeholder="Enter Tax Rate"
                        disabled={false}
                        value={formik.values.taxRate}
                        onChange={handleChange}
                        error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
                        helperText={formik.touched.taxRate && formik.errors.taxRate}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} lg={2} style={{ paddingRight: 0 }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} lg={2} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          setisEditMode(false);
                          setIsDirty(false);
                          formik.resetForm();
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allTaxTypeRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
